/**
 * oib.sequence/0.1 - portables Format fuer eine einzelne zeitbasierte Sequenz
 * (ein Pattern mit Timing, Tracks und Events).
 *
 * Dieses Format beschreibt AUSSCHLIESSLICH Zeit, Spuren und Ereignisse.
 * Es enthaelt keine Instrumentdefinition, keine Klangparameter und keine
 * backend-spezifischen Kommandos (siehe SEQUENCE_FORMAT.md und
 * OIB_INTEGRATION_CONTRACT.md).
 */

export const SEQUENCE_SCHEMA_VERSION = 'oib.sequence/0.1' as const;

export const MIN_MIDI_NOTE = 0;
export const MAX_MIDI_NOTE = 127;
export const MIN_VELOCITY = 0;
export const MAX_VELOCITY = 127;
export const MIN_CONTROL_VALUE = 0;
export const MAX_CONTROL_VALUE = 1;

export type TrackType = 'note' | 'chord' | 'drum' | 'control';

export interface TimeSignature {
  readonly numerator: number;
  readonly denominator: number;
}

export interface SequenceTiming {
  /** Schlaege pro Minute, 30..300. */
  readonly bpm: number;
  readonly timeSignature: TimeSignature;
  /** Schritte pro Schlag, z.B. 4 fuer 16tel bei 4/4. */
  readonly stepsPerBeat: number;
  /** Patternlaenge in Schritten, 1..64. */
  readonly lengthSteps: number;
  readonly loop: boolean;
  /** Swing-Betrag 0..1, optional. 0 oder fehlend = kein Swing. */
  readonly swing?: number;
}

export interface Track {
  readonly id: string;
  readonly name: string;
  /**
   * Freie Rollenbezeichnung, z.B. "chord", "bass", "lead", "drums" oder eine
   * OIB-Rollen-ID. Kein backend- oder instrumentspezifischer Sonderfall.
   */
  readonly role: string;
  readonly type: TrackType;
  readonly mute: boolean;
  readonly solo: boolean;
  /** Pegel 0..1. */
  readonly level: number;
}

interface BaseEvent {
  readonly id: string;
  /** 0-basierte Stepposition, muss < lengthSteps sein. */
  readonly step: number;
  readonly trackId: string;
}

export interface NoteEvent extends BaseEvent {
  readonly kind: 'note';
  /** MIDI-Notennummer 0..127. Genau eines von note/scaleDegree ist erforderlich. */
  readonly note?: number;
  /** Skalenstufe (1-basiert), alternative Zielangabe zu note. */
  readonly scaleDegree?: number;
  readonly velocity: number;
  /** Gate-Laenge in Schritten, > 0. */
  readonly gate: number;
}

export type ChordQuality =
  | 'major'
  | 'minor'
  | 'diminished'
  | 'augmented'
  | 'sus2'
  | 'sus4'
  | 'major7'
  | 'minor7'
  | 'dominant7';

export interface ChordEvent extends BaseEvent {
  readonly kind: 'chord';
  /** Grundton als Skalenstufe (1-basiert). */
  readonly degree: number;
  readonly quality: ChordQuality;
  readonly velocity: number;
  readonly gate: number;
}

export interface DrumEvent extends BaseEvent {
  readonly kind: 'drum';
  readonly triggerId: string;
  readonly velocity: number;
}

export interface ControlEvent extends BaseEvent {
  readonly kind: 'control';
  readonly controlId: string;
  /** Normierter Wert 0..1. */
  readonly value: number;
  /** Optionale Referenz auf einen OIB-State (rein informativ/pruefbar). */
  readonly stateRef?: string;
  /** Optionale Referenz auf eine OIB-Szene (rein informativ/pruefbar). */
  readonly sceneRef?: string;
}

export type SequenceEvent = NoteEvent | ChordEvent | DrumEvent | ControlEvent;

export interface SequenceDocument {
  readonly schemaVersion: typeof SEQUENCE_SCHEMA_VERSION;
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly description?: string;
  readonly author?: string;
  readonly tags?: readonly string[];
  readonly timing: SequenceTiming;
  readonly tracks: readonly Track[];
  readonly events: readonly SequenceEvent[];
}

export const CHORD_QUALITIES: readonly ChordQuality[] = [
  'major',
  'minor',
  'diminished',
  'augmented',
  'sus2',
  'sus4',
  'major7',
  'minor7',
  'dominant7',
];

export const TRACK_TYPES: readonly TrackType[] = ['note', 'chord', 'drum', 'control'];
